import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { AdminFeedback } from '@/components/admin/AdminFeedback';
import { AdminTraffic } from '@/components/admin/AdminTraffic';
import { AdminKaiaVideo } from '@/components/admin/AdminKaiaVideo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { UserAvatar } from '@/components/UserAvatar';
import {
  Shield, Users, Crown, Coins, Euro, Video, Search, Loader2, Zap,
  CheckCircle2, Clock, UserPlus, Trash2, Ban, Download, TrendingUp, UserCheck, CalendarClock,
  BarChart3, AlertTriangle, ChevronRight, MapPin, Rocket,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip as RTooltip, CartesianGrid,
} from 'recharts';
import { toast } from 'sonner';
import { StatCard, PlanBadge, EngagementBadge, useAdminFmt } from '@/components/AdminShared';

const EMPTY_CREATE = { name: '', email: '', password: '', credits: '5', plan: 'free', is_admin: false };

export default function Admin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale, fmtDate, fmtDateTime } = useAdminFmt();

  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [creating, setCreating] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const loadOverview = async () => {
    try { const { data } = await api.get('/admin/overview'); setOverview(data); } catch (e) { console.error('loadOverview failed', e); }
  };
  const loadUsers = async (q = '') => {
    setLoadingUsers(true);
    try { const { data } = await api.get(`/admin/users${q ? `?search=${encodeURIComponent(q)}` : ''}`); setUsers(data); setSelectedIds([]); }
    catch (e) { toast.error(apiError(e, t('admin.loadUsersError'))); }
    finally { setLoadingUsers(false); }
  };
  const loadPayments = async () => {
    setLoadingPayments(true);
    try { const { data } = await api.get('/admin/payments?limit=200'); setPayments(data); }
    catch (e) { toast.error(apiError(e, t('admin.loadPaymentsError'))); }
    finally { setLoadingPayments(false); }
  };
  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try { const { data } = await api.get('/admin/analytics'); setAnalytics(data); }
    catch (e) { toast.error(apiError(e, t('admin.loadAnalyticsError'))); }
    finally { setLoadingAnalytics(false); }
  };

  useEffect(() => {
    const init = async () => { await Promise.all([loadOverview(), loadUsers()]); };
    init();
  }, []);

  const createUser = async () => {
    const f = createForm;
    if (!f.name.trim() || !f.email.trim() || f.password.length < 6) { toast.error(t('admin.createValidation')); return; }
    const tier = f.plan === 'free' ? 'free' : f.plan.startsWith('elite') ? 'elite' : 'pro';
    const interval = f.plan === 'free' ? 'pro_monthly' : f.plan;
    setCreating(true);
    try {
      await api.post('/admin/users', {
        name: f.name.trim(), email: f.email.trim(), password: f.password,
        credits: f.credits === '' ? null : Math.max(0, parseInt(f.credits, 10) || 0),
        tier, interval, is_admin: f.is_admin,
      });
      toast.success(t('admin.created'));
      setCreateOpen(false); setCreateForm(EMPTY_CREATE);
      await Promise.all([loadOverview(), loadUsers(search)]);
    } catch (e) { toast.error(apiError(e, t('admin.createError'))); } finally { setCreating(false); }
  };

  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const { data } = await api.get('/admin/users/export.csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `kiteiq-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e, t('admin.exportError'))); } finally { setExportingCsv(false); }
  };

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const { data } = await api.post('/admin/users/bulk-delete', { ids: selectedIds });
      const protectedCount = (data.skipped || []).filter((s) => s.reason === 'admin' || s.reason === 'self').length;
      let msg = t('admin.deletedCount', { count: data.deleted_count });
      if (protectedCount) msg += ` \u00b7 ${t('admin.adminSkipped', { count: protectedCount })}`;
      toast.success(msg);
      setSelectedIds([]);
      await Promise.all([loadOverview(), loadUsers(search)]);
    } catch (e) { toast.error(apiError(e, t('admin.deleteUsersError'))); } finally { setBulkDeleting(false); }
  };

  const toggleOne = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const onSearchSubmit = (e) => { e.preventDefault(); loadUsers(search); };

  const selectableIds = users.filter((u) => !u.is_admin).map((u) => u.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;
  const toggleAll = () => setSelectedIds(allSelected ? [] : selectableIds);

  return (
    <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow text-[#FFB020] mb-2 flex items-center gap-2"><Shield className="w-4 h-4" /> {t('admin.eyebrow')}</div>
          <h1 className="font-display text-4xl sm:text-5xl">{t('admin.title')}</h1>
          <p className="text-muted-foreground mt-2">{t('admin.subtitle')}</p>
        </div>
        <Button
          onClick={() => navigate('/admin/growth-plan')}
          className="bg-[#2EE59D] hover:bg-[#48CAE4] text-black font-semibold rounded-xl shrink-0"
          data-testid="admin-growth-plan-btn"
        >
          <Rocket className="w-4 h-4 mr-2" /> {t('admin.growthPlanBtn')}
        </Button>
      </div>

      {/* Overview stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
        {!overview ? (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 bg-white/10 rounded-2xl" />)
        ) : (
          <>
            <StatCard icon={Users} label={t('admin.stats.users')} value={overview.total_users} testid="stat-users" />
            <StatCard icon={Crown} label={t('admin.stats.pro')} value={overview.pro_users} accent="#FF6B35" testid="stat-pro" />
            <StatCard icon={Users} label={t('admin.stats.free')} value={overview.free_users} accent="#48CAE4" testid="stat-free" />
            <StatCard icon={Euro} label={t('admin.stats.revenue')} value={`€${overview.revenue_eur.toFixed(2)}`} accent="#2EE59D" testid="stat-revenue" />
            <StatCard icon={Coins} label={t('admin.stats.creditsOut')} value={overview.credits_outstanding} accent="#FFB020" testid="stat-credits" />
            <StatCard icon={Video} label={t('admin.stats.analyses')} value={overview.total_analyses} accent="#48CAE4" testid="stat-analyses" />
          </>
        )}
      </div>

      <Tabs defaultValue="users" className="mt-8" onValueChange={(v) => { if (v === 'payments' && payments.length === 0) loadPayments(); if (v === 'analytics' && !analytics) loadAnalytics(); }}>
        <TabsList className="bg-[#12161B] border border-white/10">
          <TabsTrigger value="users" data-testid="admin-tab-users">{t('admin.tabUsers')}</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="admin-tab-analytics">{t('admin.tabAnalytics')}</TabsTrigger>
          <TabsTrigger value="traffic" data-testid="admin-tab-traffic">{t('admin.tabTraffic')}</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">{t('admin.tabPayments')}</TabsTrigger>
          <TabsTrigger value="feedback" data-testid="admin-tab-feedback">{t('feedbackAdmin.tab')}</TabsTrigger>
          <TabsTrigger value="kaia" data-testid="admin-tab-kaia">{t('adminKaia.tab')}</TabsTrigger>
        </TabsList>

        {/* USERS */}
        <TabsContent value="users" className="mt-5">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <form onSubmit={onSearchSubmit} className="flex gap-2 flex-1 min-w-[260px] max-w-md">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('admin.searchPlaceholder')} className="pl-9 bg-[#0A0A0A] border-white/15" data-testid="admin-user-search" />
              </div>
              <Button type="submit" className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="admin-search-btn">{t('admin.searchBtn')}</Button>
            </form>

            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={exportCsv} disabled={exportingCsv} className="bg-transparent border-white/15 hover:bg-white/10 rounded-xl" data-testid="admin-export-csv">
                {exportingCsv ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-2" /> {t('admin.exportCsv')}</>}
              </Button>
              <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setCreateForm(EMPTY_CREATE); }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#FF6B35] hover:bg-[#FFB020] text-black font-semibold rounded-xl" data-testid="admin-create-user-btn"><UserPlus className="w-4 h-4 mr-2" /> {t('admin.newUser')}</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0C0F13] border-white/10 text-white" data-testid="admin-create-dialog">
                  <DialogHeader>
                    <DialogTitle>{t('admin.createTitle')}</DialogTitle>
                    <DialogDescription className="text-muted-foreground">{t('admin.createDesc')}</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm">{t('admin.name')}</Label>
                      <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder={t('admin.namePlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="create-name" />
                    </div>
                    <div>
                      <Label className="text-sm">{t('admin.email')}</Label>
                      <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder={t('admin.emailPlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="create-email" />
                    </div>
                    <div>
                      <Label className="text-sm">{t('admin.password')}</Label>
                      <Input type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder={t('admin.passwordPlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="create-password" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">{t('admin.startCredits')}</Label>
                        <Input type="number" value={createForm.credits} onChange={(e) => setCreateForm({ ...createForm, credits: e.target.value })} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="create-credits" />
                      </div>
                      <div>
                        <Label className="text-sm">{t('admin.plan')}</Label>
                        <Select value={createForm.plan} onValueChange={(v) => setCreateForm({ ...createForm, plan: v })}>
                          <SelectTrigger className="mt-1.5 bg-[#12161B] border-white/15" data-testid="create-plan"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="free">{t('admin.planFree')}</SelectItem>
                            <SelectItem value="pro_monthly">{t('admin.planProMonthly')}</SelectItem>
                            <SelectItem value="pro_yearly">{t('admin.planProYearly')}</SelectItem>
                            <SelectItem value="elite_monthly">{t('admin.planEliteMonthly')}</SelectItem>
                            <SelectItem value="elite_yearly">{t('admin.planEliteYearly')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center justify-between rounded-xl bg-[#12161B] border border-white/10 px-3 py-2.5">
                      <div className="flex items-center gap-2 text-sm"><Shield className="w-4 h-4 text-[#FFB020]" /> {t('admin.makeAdmin')}</div>
                      <Switch checked={createForm.is_admin} onCheckedChange={(v) => setCreateForm({ ...createForm, is_admin: v })} data-testid="create-admin-switch" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateOpen(false)} className="bg-transparent border-white/15 hover:bg-white/10">{t('admin.cancel')}</Button>
                    <Button onClick={createUser} disabled={creating} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold" data-testid="create-submit">
                      {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.createUser')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Bulk action bar */}
          {selectedIds.length > 0 && (
            <div className="flex items-center justify-between gap-3 mb-3 rounded-xl border border-[#FF6B35]/30 bg-[#FF6B35]/5 px-4 py-2.5" data-testid="admin-bulk-bar">
              <span className="text-sm"><span className="font-data font-semibold text-[#FF6B35]" data-testid="admin-bulk-count">{selectedIds.length}</span> {t('admin.selected')}</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} className="text-muted-foreground hover:text-white" data-testid="admin-bulk-clear">{t('admin.clear')}</Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" className="bg-[#FF6B35] hover:bg-[#FFB020] text-black font-semibold rounded-lg" data-testid="admin-bulk-delete-btn"><Trash2 className="w-4 h-4 mr-2" /> {t('admin.deleteSelected')}</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#12161B] border-white/15 text-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('admin.bulkTitle', { count: selectedIds.length })}</AlertDialogTitle>
                      <AlertDialogDescription className="text-muted-foreground">{t('admin.bulkDesc')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-transparent border-white/15 hover:bg-white/10">{t('admin.cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={bulkDelete} disabled={bulkDeleting} className="bg-[#FF6B35] hover:bg-[#FFB020] text-black" data-testid="admin-bulk-delete-confirm">
                        {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.deletePermanently')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}

          <div className="kite-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="w-10">
                    <Checkbox checked={allSelected || (someSelected ? 'indeterminate' : false)} onCheckedChange={toggleAll} aria-label={t('admin.selectAll')} data-testid="admin-select-all" />
                  </TableHead>
                  <TableHead className="text-muted-foreground">{t('admin.colUser')}</TableHead>
                  <TableHead className="text-muted-foreground">{t('admin.colPlan')}</TableHead>
                  <TableHead className="text-muted-foreground hidden md:table-cell">{t('admin.colStatus')}</TableHead>
                  <TableHead className="text-muted-foreground text-right">{t('admin.colCredits')}</TableHead>
                  <TableHead className="text-muted-foreground text-right hidden xl:table-cell">{t('admin.colLogins')}</TableHead>
                  <TableHead className="text-muted-foreground hidden lg:table-cell">{t('admin.colLastLogin')}</TableHead>
                  <TableHead className="text-muted-foreground hidden sm:table-cell">{t('admin.colJoined')}</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingUsers ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00B4D8]" /></TableCell></TableRow>
                ) : users.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">{t('admin.noUsers')}</TableCell></TableRow>
                ) : users.map((u) => (
                  <TableRow key={u.id} className="border-white/8 cursor-pointer hover:bg-white/5 group" onClick={() => navigate(`/admin/users/${u.id}`)} data-testid="admin-user-row">
                    <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selectedIds.includes(u.id)} disabled={u.is_admin} onCheckedChange={() => toggleOne(u.id)} aria-label={`Select ${u.email}`} data-testid="admin-row-checkbox" />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <UserAvatar src={null} name={u.name || u.email} size={32} />
                        <div className="min-w-0">
                          <div className="font-medium truncate flex items-center gap-1.5">
                            {u.name || '\u2014'}
                            {u.is_admin && <Shield className="w-3 h-3 text-[#FFB020]" />}
                            {u.suspended && <Ban className="w-3 h-3 text-[#FF6B35]" />}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><PlanBadge tier={u.tier} /></TableCell>
                    <TableCell className="hidden md:table-cell">
                      {u.suspended
                        ? <Badge variant="outline" className="bg-[#FF6B35]/15 text-[#FF6B35] border-[#FF6B35]/30 text-[11px]"><Ban className="w-3 h-3 mr-1" />{t('admin.suspendedBadge')}</Badge>
                        : <EngagementBadge status={u.engagement_status} t={t} />}
                    </TableCell>
                    <TableCell className="text-right font-data font-semibold text-[#00B4D8]">{u.credits}</TableCell>
                    <TableCell className="text-right font-data text-muted-foreground hidden xl:table-cell">{u.login_count ?? 0}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">{u.last_login_at ? fmtDateTime(u.last_login_at) : '\u2014'}</TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                    <TableCell className="w-8 text-muted-foreground"><ChevronRight className="w-4 h-4 opacity-40 group-hover:opacity-100 transition-opacity" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* PAYMENTS */}
        <TabsContent value="payments" className="mt-5">
          <div className="kite-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-muted-foreground">{t('admin.colUser')}</TableHead>
                  <TableHead className="text-muted-foreground">{t('admin.colDescription')}</TableHead>
                  <TableHead className="text-muted-foreground text-right">{t('admin.colAmount')}</TableHead>
                  <TableHead className="text-muted-foreground">{t('admin.colStatus')}</TableHead>
                  <TableHead className="text-muted-foreground hidden sm:table-cell">{t('admin.colDate')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingPayments ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00B4D8]" /></TableCell></TableRow>
                ) : payments.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">{t('admin.noPaymentsTable')}</TableCell></TableRow>
                ) : payments.map((p) => (
                  <TableRow key={p.id} className="border-white/8" data-testid="admin-payment-row">
                    <TableCell className="text-sm">{p.user_email || '\u2014'}</TableCell>
                    <TableCell className="text-sm">{p.description}{p.demo && <span className="text-xs text-muted-foreground"> · {t('profile.test')}</span>}</TableCell>
                    <TableCell className="text-right font-data font-semibold">€{Number(p.amount_eur || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={p.status === 'paid' ? 'border-[#2EE59D]/40 text-[#2EE59D]' : 'border-white/20 text-muted-foreground'}>
                        {p.status === 'paid' ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}{t(`profile.status.${p.status}`, { defaultValue: p.status })}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{fmtDate(p.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ANALYTICS */}
        <TabsContent value="analytics" className="mt-5">
          {loadingAnalytics || !analytics ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 bg-white/10 rounded-2xl" />)}
            </div>
          ) : (
            <div className="space-y-5" data-testid="admin-analytics">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard icon={TrendingUp} label={t('admin.stats.mrr')} value={`€${(overview?.mrr_eur ?? 0).toFixed(2)}`} accent="#2EE59D" testid="stat-mrr" />
                <StatCard icon={TrendingUp} label={t('admin.stats.arr')} value={`€${(overview?.arr_eur ?? 0).toFixed(2)}`} accent="#2EE59D" testid="stat-arr" />
                <StatCard icon={UserCheck} label={t('admin.stats.active7')} value={overview?.active_users_7d ?? 0} accent="#00B4D8" testid="stat-active7" />
                <StatCard icon={CalendarClock} label={t('admin.stats.active30')} value={overview?.active_users_30d ?? 0} accent="#48CAE4" testid="stat-active30" />
                <StatCard icon={Ban} label={t('admin.stats.churn')} value={analytics.churn} accent="#FF6B35" testid="stat-churn" />
                <StatCard icon={AlertTriangle} label={t('admin.stats.failed')} value={overview?.failed_analyses ?? 0} accent="#FFB020" testid="stat-failed" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="kite-card p-5">
                  <div className="flex items-center gap-2 mb-4"><BarChart3 className="w-4 h-4 text-[#00B4D8]" /><span className="label-eyebrow text-muted-foreground">{t('admin.analyticsCharts.analysesPerDay')}</span></div>
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={analytics.analyses_timeseries} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                      <defs>
                        <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00B4D8" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#00B4D8" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="date" tick={{ fill: '#8b949e', fontSize: 11 }} tickFormatter={(d) => new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'short' })} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={{ fill: '#8b949e', fontSize: 11 }} />
                      <RTooltip contentStyle={{ background: '#12161B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff' }} labelFormatter={(d) => new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })} />
                      <Area type="monotone" dataKey="count" stroke="#00B4D8" strokeWidth={2} fill="url(#ag)" name={t('admin.stats.analyses')} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="kite-card p-5">
                  <div className="flex items-center gap-2 mb-4"><MapPin className="w-4 h-4 text-[#2EE59D]" /><span className="label-eyebrow text-muted-foreground">{t('admin.analyticsCharts.topSpots')}</span></div>
                  {analytics.top_spots.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-10 text-center">{t('admin.analyticsCharts.noData')}</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={analytics.top_spots} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} tick={{ fill: '#8b949e', fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" width={110} tick={{ fill: '#8b949e', fontSize: 11 }} />
                        <RTooltip contentStyle={{ background: '#12161B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff' }} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                        <Bar dataKey="count" fill="#2EE59D" radius={[0, 6, 6, 0]} name={t('admin.stats.analyses')} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="kite-card p-5">
                  <div className="flex items-center gap-2 mb-4"><Users className="w-4 h-4 text-[#48CAE4]" /><span className="label-eyebrow text-muted-foreground">{t('admin.analyticsCharts.funnel')}</span></div>
                  <div className="space-y-3">
                    {analytics.funnel.map((f) => {
                      const max = analytics.funnel[0]?.count || 1;
                      const pct = Math.round(100 * f.count / max);
                      return (
                        <div key={f.stage}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-muted-foreground">{t(`admin.funnelStage.${f.stage}`)}</span>
                            <span className="font-data font-semibold">{f.count}</span>
                          </div>
                          <div className="h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-[#00B4D8]" style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="kite-card p-5">
                  <div className="flex items-center gap-2 mb-4"><Coins className="w-4 h-4 text-[#FFB020]" /><span className="label-eyebrow text-muted-foreground">{t('admin.analyticsCharts.creditEconomy')}</span></div>
                  <div className="space-y-2.5 text-sm">
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('admin.credits.outstanding')}</span><span className="font-data font-semibold text-[#FFB020]">{analytics.credit_economy.outstanding}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('admin.credits.consumed')}</span><span className="font-data font-semibold">{analytics.credit_economy.consumed}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('admin.credits.purchased')}</span><span className="font-data font-semibold">{analytics.credit_economy.purchased}</span></div>
                    <div className="flex items-center justify-between border-t border-white/10 pt-2.5"><span className="text-muted-foreground">{t('admin.credits.aiCost')}</span><span className="font-data font-semibold text-[#2EE59D]">€{analytics.estimated_ai_cost_eur.toFixed(2)}</span></div>
                  </div>
                </div>

                <div className="kite-card p-5">
                  <div className="flex items-center gap-2 mb-4"><Zap className="w-4 h-4 text-[#FF6B35]" /><span className="label-eyebrow text-muted-foreground">{t('admin.analyticsCharts.gearUsage')}</span></div>
                  <div className="space-y-2.5 text-sm">
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('admin.gear.total')}</span><span className="font-data font-semibold">{analytics.gear_usage.total}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('admin.gear.last30')}</span><span className="font-data font-semibold text-[#00B4D8]">{analytics.gear_usage.last_30d}</span></div>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">{t('admin.gear.anonymous')}</span><span className="font-data font-semibold">{analytics.gear_usage.anonymous}</span></div>
                    {analytics.gear_usage.top_locations.length > 0 && (
                      <div className="border-t border-white/10 pt-2.5">
                        <div className="text-xs text-muted-foreground mb-1.5">{t('admin.gear.topLocations')}</div>
                        {analytics.gear_usage.top_locations.slice(0, 3).map((l) => (
                          <div key={l.name} className="flex items-center justify-between"><span className="truncate">{l.name}</span><span className="font-data text-muted-foreground">{l.count}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* FEEDBACK */}
        <TabsContent value="feedback" className="mt-5">
          <AdminFeedback />
        </TabsContent>

        {/* TRAFFIC */}
        <TabsContent value="traffic" className="mt-5">
          <AdminTraffic />
        </TabsContent>

        {/* KAIA VIDEOS */}
        <TabsContent value="kaia" className="mt-5">
          <AdminKaiaVideo />
        </TabsContent>
      </Tabs>
    </div>
  );
}
